'use client';

import { useContext, useEffect, useId, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { geistMono } from 'utils/fonts';
import type { Currency } from 'utils/Currency';
import type { VoucherOffer } from 'lib/gifting/voucherOffer';
import {
  useCartStore,
  useShallow,
} from 'components/shared/Cart/store/CartStoreContext';
import { ProductType } from 'components/shared/Cart/types';
import { GIFT_CARD_LINE } from 'lib/commerce/cart/giftCardLine';
import {
  VOUCHER_DEFAULT_FINISH,
  VOUCHER_DESIGN_NAMES,
  VOUCHER_FINISHES,
  VOUCHER_MESSAGE_MAX,
  VOUCHER_SCHEDULE_MAX_DAYS,
  isIllustratedFinish,
  voucherSwatchFile,
  type IllustratedFinish,
  type VoucherFinish,
} from 'content/gifting/voucherConfig';
import { formatGiftingPrice } from 'lib/gifting/format';
import { GlobalUIContext } from 'contexts/GlobalUIContext';
import { GIFTING_HEADER_FALLBACK_PX } from 'components/gifting/shared/headerOffset';
import { voucherAssetUrl } from 'components/gifting/voucher/assets';
import { VoucherCard } from './VoucherCard';
import { CardStage } from './cards/CardStage';
import { NoteCard } from './customizer/NoteCard';
import { CalendarIcon } from './customizer/CalendarIcon';
import styles from './VoucherCustomizer.module.css';

type When = 'now' | 'pick';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const money = (amount: number, currency: Currency, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const moneyWhole = (amount: number, currency: Currency, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(amount);

const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

export const VoucherCustomizer = ({
  offer,
}: {
  offer: VoucherOffer | null;
}) => {
  const t = useTranslations('gifting');
  const locale = useLocale();
  // The sticky card sits under the header, like the other sticky surfaces.
  const { headerHeight } = useContext(GlobalUIContext);
  const headerOffset = {
    '--gfn': `${headerHeight ?? GIFTING_HEADER_FALLBACK_PX}px`,
  } as React.CSSProperties;
  const uid = useId();
  const addToCart = useCartStore(useShallow((s) => s.addToCart));

  // The selected gift card variant; Ring PRO's amount by default.
  const [amountKey, setAmountKey] = useState<string>(
    () =>
      offer?.amounts.find((a) => a.ring === 'pro')?.variantId ??
      offer?.amounts[0]?.variantId ??
      '',
  );
  const [finish, setFinish] = useState<VoucherFinish>(VOUCHER_DEFAULT_FINISH);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [message, setMessage] = useState('');
  const [when, setWhen] = useState<When>('now');
  const [date, setDate] = useState('');
  const [today, setToday] = useState('');
  const [lastDay, setLastDay] = useState('');
  const [adding, setAdding] = useState(false);
  const [addFailed, setAddFailed] = useState(false);

  // Today is the visitor's local date, so it's set after hydration.
  useEffect(() => {
    const now = new Date();
    const inDays = (n: number) =>
      isoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + n));
    setToday(isoDate(now));
    setDate(inDays(7));
    setLastDay(inDays(VOUCHER_SCHEDULE_MAX_DAYS));
  }, []);

  if (!offer) {
    return (
      <section className={styles.gift} style={headerOffset} id="customize">
        <div className={styles.aside}>
          <div className={styles.asideCard}>
            <VoucherCard
              finish="aster-black"
              title={t('voucher.customizer.card.title')}
              subtitle={t('voucher.customizer.card.for', {
                name: t('voucher.customizer.card.someone'),
              })}
              label={t('voucher.customizer.card.label')}
              className={styles.card}
            />
          </div>
        </div>
        <div className={styles.main}>
          <h2 className={styles.title}>{t('voucher.customizer.title')}</h2>
          <div className={styles.unavailable}>
            <h3 className={styles.unavailableTitle}>
              {t('buy.unavailable.title')}
            </h3>
            <p className={styles.unavailableText}>
              {t('buy.unavailable.description')}
            </p>
          </div>
        </div>
      </section>
    );
  }

  const { currency } = offer;
  const fmt = (n: number) => formatGiftingPrice(n, currency, locale);
  const ringTag = {
    air: t('voucher.customizer.amount.ringAir'),
    pro: t('voucher.customizer.amount.ringPro'),
  };
  const presets = offer.amounts.map((a) => ({
    key: a.variantId,
    value: a.amount,
    tag: a.ring ? ringTag[a.ring] : undefined,
  }));

  const selected = offer.amounts.find((a) => a.variantId === amountKey);
  const amount = selected?.amount ?? 0;
  const model = selected?.ring ? ringTag[selected.ring] : null;

  const to = name.trim();
  const em = email.trim();
  const msg = message.trim();
  const nameValid = to.length > 0;
  const nameInvalid = nameTouched && !nameValid;
  const emailValid = EMAIL_RE.test(em);
  const emailInvalid = emailTouched && !emailValid;
  const dateInvalid =
    when === 'pick' &&
    (!date || (!!today && date < today) || (!!lastDay && date > lastDay));
  const canAdd = !!selected && !adding;

  const addGiftCard = async () => {
    setNameTouched(true);
    setEmailTouched(true);
    if (!selected || !nameValid || !emailValid || dateInvalid) {
      const first = !nameValid ? ids.name : !emailValid ? ids.email : ids.date;
      document.getElementById(first)?.focus();
      return;
    }
    const attrs: Record<string, string> = {
      [GIFT_CARD_LINE.sendToRecipient]: 'true',
      [GIFT_CARD_LINE.recipientEmail]: em,
      [GIFT_CARD_LINE.recipientName]: to.slice(0, 255),
      [GIFT_CARD_LINE.message]: msg.slice(0, VOUCHER_MESSAGE_MAX),
      ...(when === 'pick' ? { [GIFT_CARD_LINE.sendOn]: date } : {}),
      [GIFT_CARD_LINE.offset]: String(new Date().getTimezoneOffset()),
      [GIFT_CARD_LINE.design]: VOUCHER_DESIGN_NAMES[finish],
    };
    setAdding(true);
    setAddFailed(false);
    try {
      // A refused add shows the cart's own toast; only a throw lands in catch.
      await addToCart(
        [
          {
            shopifyVariantId: selected.variantId,
            productType: ProductType.GIFT_CARD,
            // Empty values (no name, no message) are dropped on the way in.
            params: {
              noteAttributes: Object.entries(attrs).map(([key, value]) => ({
                key,
                value,
              })),
            },
          },
        ],
        false,
      );
    } catch {
      setAddFailed(true);
    } finally {
      setAdding(false);
    }
  };
  const finishNames: Record<VoucherFinish, string> = {
    holidays: t('voucher.customizer.finish.holidays'),
    diwali: t('voucher.customizer.finish.diwali'),
    birthday: t('voucher.customizer.finish.birthday'),
    baby: t('voucher.customizer.finish.baby'),
    marble: t('voucher.customizer.finish.marble'),
    halftone: t('voucher.customizer.finish.halftone'),
    prism: t('voucher.customizer.finish.prism'),
    'aster-black': t('voucher.customizer.finish.aster-black'),
    'bionic-gold': t('voucher.customizer.finish.bionic-gold'),
    'space-silver': t('voucher.customizer.finish.space-silver'),
  };
  const finishName = finishNames[finish];
  // Illustrated cards carry their occasion; the rest read "Gift voucher".
  const occasionTitles: Record<IllustratedFinish, string> = {
    holidays: t('voucher.customizer.card.occasion.holidays'),
    diwali: t('voucher.customizer.card.occasion.diwali'),
    birthday: t('voucher.customizer.card.occasion.birthday'),
    baby: t('voucher.customizer.card.occasion.baby'),
  };
  // Per finish, so a card that's transitioning out keeps its own labels.
  const cardProps = (f: VoucherFinish) => {
    return {
      title: isIllustratedFinish(f)
        ? occasionTitles[f]
        : t('voucher.customizer.card.title'),
      // Every card names only the recipient.
      subtitle: t('voucher.customizer.card.for', {
        name: to || t('voucher.customizer.card.someone'),
      }),
      label: t('voucher.customizer.card.label'),
      amount: moneyWhole(amount, currency, locale),
    };
  };

  const deliveryText =
    when === 'pick' && date
      ? t('voucher.customizer.review.delivery.date', {
          date: new Date(`${date}T08:00:00`).toLocaleDateString(locale, {
            month: 'short',
            day: 'numeric',
          }),
        })
      : t('voucher.customizer.review.delivery.now');

  const ids = {
    name: `${uid}-name`,
    email: `${uid}-email`,
    nameErr: `${uid}-name-err`,
    emailErr: `${uid}-email-err`,
    note: `${uid}-note`,
    date: `${uid}-date`,
    dateErr: `${uid}-date-err`,
  };

  return (
    <section
      className={`${styles.gift} ${geistMono.variable}`}
      style={headerOffset}
      id="customize"
    >
      <div className={styles.aside}>
        <div className={styles.asideCard}>
          <CardStage
            finish={finish}
            cardProps={cardProps}
            cardClassName={styles.card}
          />
        </div>
      </div>

      <div className={styles.main}>
        <h2 className={styles.title}>{t('voucher.customizer.title')}</h2>
        <p className={styles.eyebrow}>{t('voucher.customizer.eyebrow')}</p>

        <form
          className={styles.steps}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (canAdd) addGiftCard();
          }}
        >
          <section className={styles.step}>
            <h3 className={styles.stepTitle}>
              {t('voucher.customizer.amount.title')}
            </h3>
            <p className={styles.stepSub}>
              {t('voucher.customizer.amount.subtitle')}
            </p>
            <div className={styles.stepBody}>
              <div
                className={styles.amts}
                role="radiogroup"
                aria-label={t('voucher.customizer.amount.groupLabel')}
              >
                {presets.map((p) => (
                  <label key={p.key} className={styles.amt}>
                    <input
                      type="radio"
                      name={`${uid}-amt`}
                      className={styles.hiddenInput}
                      checked={amountKey === p.key}
                      onChange={() => setAmountKey(p.key)}
                    />
                    <span
                      className={`${styles.amtBox} ${
                        p.tag ? styles.amtTagged : ''
                      }`}
                    >
                      {fmt(p.value)}
                      {p.tag && (
                        <small className={styles.amtTag}>{p.tag}</small>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </section>

          <section className={styles.step}>
            <h3 className={styles.stepTitle}>
              {t('voucher.customizer.finish.title')}
            </h3>
            <p className={styles.stepSub}>
              {t('voucher.customizer.finish.subtitle')}
            </p>
            <div className={styles.stepBody}>
              <div
                className={styles.swatches}
                role="radiogroup"
                aria-label={t('voucher.customizer.finish.groupLabel')}
              >
                {VOUCHER_FINISHES.map((f) => (
                  <label
                    key={f}
                    className={styles.swatch}
                    title={finishNames[f]}
                  >
                    <input
                      type="radio"
                      name={`${uid}-finish`}
                      className={styles.hiddenInput}
                      checked={finish === f}
                      onChange={() => setFinish(f)}
                    />
                    <i
                      className={styles.swatchDot}
                      style={
                        {
                          '--sw': `url(${voucherAssetUrl(
                            voucherSwatchFile(f),
                          )})`,
                        } as React.CSSProperties
                      }
                    />
                    <span className={styles.sr}>{finishNames[f]}</span>
                  </label>
                ))}
              </div>
              <p key={finish} className={styles.swatchName}>
                {finishName}
              </p>
            </div>
          </section>

          <section className={`${styles.step} ${styles.stepNote}`}>
            <h3 className={styles.stepTitle}>
              {t('voucher.customizer.who.title')}
            </h3>
            <p className={styles.stepSub}>
              {t('voucher.customizer.who.subtitle')}
            </p>
            <div className={styles.stepBody}>
              <div className={styles.fRow}>
                <div className={styles.f}>
                  <label htmlFor={ids.name} className={styles.fLabel}>
                    {t('voucher.customizer.who.name')}
                  </label>
                  <input
                    id={ids.name}
                    className={styles.input}
                    type="text"
                    autoComplete="off"
                    placeholder={t('voucher.customizer.who.namePlaceholder')}
                    value={name}
                    aria-invalid={nameInvalid}
                    aria-describedby={nameInvalid ? ids.nameErr : undefined}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => setNameTouched(true)}
                  />
                  {nameInvalid && (
                    <span
                      id={ids.nameErr}
                      className={`${styles.hint} ${styles.error} ${styles.fieldError}`}
                    >
                      {t('voucher.customizer.who.nameError')}
                    </span>
                  )}
                </div>
                <div className={styles.f}>
                  <label htmlFor={ids.email} className={styles.fLabel}>
                    {t('voucher.customizer.who.email')}
                  </label>
                  <input
                    id={ids.email}
                    className={styles.input}
                    type="email"
                    autoComplete="off"
                    placeholder={t('voucher.customizer.who.emailPlaceholder')}
                    value={email}
                    aria-invalid={emailInvalid}
                    aria-describedby={emailInvalid ? ids.emailErr : undefined}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                  />
                  {emailInvalid && (
                    <span
                      id={ids.emailErr}
                      className={`${styles.hint} ${styles.error} ${styles.fieldError}`}
                    >
                      {t('voucher.customizer.who.emailError')}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.f}>
                <label htmlFor={ids.note} className={styles.fLabel}>
                  {t('voucher.customizer.who.message')}
                </label>
                <NoteCard id={ids.note} value={message} onChange={setMessage} />
              </div>
            </div>
          </section>

          <section className={styles.step}>
            <h3 className={styles.stepTitle}>
              {t('voucher.customizer.when.title')}
            </h3>
            <p className={styles.stepSub}>
              {t('voucher.customizer.when.subtitle')}
            </p>
            <div className={styles.stepBody}>
              <div
                className={styles.when}
                role="radiogroup"
                aria-label={t('voucher.customizer.when.groupLabel')}
              >
                {(['now', 'pick'] as const).map((w) => (
                  <label key={w} className={styles.whenOpt}>
                    <input
                      type="radio"
                      name={`${uid}-when`}
                      className={styles.hiddenInput}
                      checked={when === w}
                      onChange={() => setWhen(w)}
                    />
                    <span className={styles.whenPill}>
                      {w === 'now'
                        ? t('voucher.customizer.when.now')
                        : t('voucher.customizer.when.pick')}
                    </span>
                  </label>
                ))}
              </div>
              <div
                className={`${styles.reveal} ${
                  when === 'pick' ? styles.revealOpen : ''
                }`}
                inert={when !== 'pick'}
              >
                <div className={styles.revealIn}>
                  <div className={styles.dateRow}>
                    <div className={styles.fInline}>
                      <label htmlFor={ids.date} className={styles.fLabel}>
                        {t('voucher.customizer.when.dateLabel')}
                      </label>
                      <span className={styles.dateField}>
                        <input
                          id={ids.date}
                          className={styles.dateInput}
                          type="date"
                          min={today || undefined}
                          max={lastDay || undefined}
                          value={date}
                          aria-invalid={dateInvalid}
                          aria-describedby={
                            dateInvalid ? ids.dateErr : undefined
                          }
                          onChange={(e) => setDate(e.target.value)}
                        />
                        <CalendarIcon className={styles.cal} />
                      </span>
                    </div>
                    {dateInvalid && (
                      <span
                        id={ids.dateErr}
                        className={`${styles.hint} ${styles.error} ${styles.fieldError}`}
                      >
                        {t('voucher.customizer.when.dateError')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className={`${styles.step} ${styles.stepReview}`}>
            <h3 className={styles.stepTitle}>
              {t('voucher.customizer.review.title')}
            </h3>
            <p className={styles.stepSub}>
              {t('voucher.customizer.review.subtitle')}
            </p>
            <div className={styles.stepBody}>
              <div className={styles.summary} aria-live="polite">
                <h4 className={styles.summaryTitle}>
                  {t('voucher.customizer.review.productTitle')}
                </h4>
                <p className={styles.summaryVariant}>
                  {model ?? t('voucher.customizer.review.variantVoucher')}
                </p>
                <dl className={styles.rows}>
                  <div className={styles.row}>
                    <dt>{t('voucher.customizer.review.rows.amount')}</dt>
                    <dd className={styles.dd}>
                      {money(amount, currency, locale)}
                    </dd>
                  </div>
                  <div className={styles.row}>
                    <dt>{t('voucher.customizer.review.rows.design')}</dt>
                    <dd className={styles.dd}>{finishName}</dd>
                  </div>
                  <div className={styles.row}>
                    <dt>{t('voucher.customizer.review.rows.to')}</dt>
                    <dd className={styles.dd}>
                      {to || t('voucher.customizer.review.someone')}
                    </dd>
                  </div>
                  <div className={styles.row}>
                    <dt>{t('voucher.customizer.review.rows.email')}</dt>
                    <dd className={`${styles.dd} ${em ? '' : styles.empty}`}>
                      {em || t('voucher.customizer.review.emptyEmail')}
                    </dd>
                  </div>
                  <div className={styles.row}>
                    <dt>{t('voucher.customizer.review.rows.message')}</dt>
                    <dd
                      className={`${styles.dd} ${styles.msg} ${
                        msg ? '' : styles.empty
                      }`}
                    >
                      {msg || t('voucher.customizer.review.emptyMessage')}
                    </dd>
                  </div>
                  <div className={styles.row}>
                    <dt>{t('voucher.customizer.review.rows.delivery')}</dt>
                    <dd className={styles.dd}>{deliveryText}</dd>
                  </div>
                </dl>
                <div className={styles.total}>
                  <div>
                    <span className={styles.totalLabel}>
                      {t('voucher.customizer.review.total')}
                    </span>
                    <small className={styles.totalNote}>
                      {t('voucher.customizer.review.noTax')}
                    </small>
                  </div>
                  <strong className={styles.totalValue}>
                    {money(amount, currency, locale)}
                  </strong>
                </div>
                <button
                  type="submit"
                  className={styles.cta}
                  disabled={!canAdd}
                  aria-busy={adding}
                >
                  <span>
                    {t('voucher.customizer.review.cta', {
                      amount: fmt(amount),
                    })}
                  </span>
                </button>
                {addFailed ? (
                  <p
                    className={`${styles.summaryNote} ${styles.error}`}
                    role="alert"
                  >
                    {t('voucher.customizer.review.addError')}
                  </p>
                ) : null}
                <p className={styles.summaryNote} role="status">
                  {t('voucher.customizer.review.taxNote')}
                </p>
              </div>
            </div>
          </section>
        </form>
      </div>
    </section>
  );
};
